<template>
  <div class="tag-page__wrapper">
    <a-lazy-hydrate when-visible>
      <a-breadcrumbs :settings="breadcrumbsSettings" :prettify-names="false" />
    </a-lazy-hydrate>

    <tag-info
      :key="
        $_lazyHydratedComponentState_getHydratedComponentRefreshKey(
          $options.consts.HYDRATED_SECTION_NAME.TAG_INFO
        )
      "
      v-bind="tag"
      :hide-info="isInitialPagePaginated"
      class="tag-page__tag-info"
      @update-follow-tag-value="updateIsFollowedTagValues"
      @mounted="
        $_lazyHydratedComponentState_onHydratedComponentMounted(
          $options.consts.HYDRATED_SECTION_NAME.TAG_INFO
        )
      "
    />

    <div v-if="articles.length" class="tag-page__container">
      <a-article-list :articles="articles">
        <template v-slot="{ article }">
          <a-lazy-hydrate
            when-visible
            :key="
              $_lazyHydratedComponentState_getHydratedComponentRefreshKey(
                article.articleId
              )
            "
          >
            <a-article-slot
              v-bind="getArticleSlotProps(article)"
              :title-html-tag="HTML_TAG.HEADING_H2"
              class="tag-page__article"
              @mounted="
                $_lazyHydratedComponentState_onHydratedComponentMounted(
                  article.articleId
                )
              "
            />
          </a-lazy-hydrate>
        </template>
        <template v-slot:banner="{ position }">
          <a-visibility
            v-if="isBannerVisible(position)"
            show
            :on="[$breakpoint.mobile]"
          >
            <a-banner
              :banner-settings="getBannerSettings(position)"
              :wrapped-in-card="true"
              class="tag-page__banner"
            />
          </a-visibility>
        </template>
      </a-article-list>
      <a-view-more
        :page="pagination.page"
        :pages-count="pagesCount"
        :in-progress="isRequestInProgress"
        class="tag-page__view-more"
        :view-more-style="$options.consts.VIEW_MORE_STYLE.BUTTON"
        @click="getNextArticleChunk"
      />
      <p v-if="isNoNewsMessageVisible" class="tag-page__no-news-found">
        No articles found
      </p>
    </div>

    <a-url-pagination
      :item-selector="ARTICLE_SLOT_SELECTOR"
      :item-count="articles.length"
      :initial-page="initialPage"
      :pagination.sync="pagination"
      :pages-count="pagesCount"
      :non-paginated-url="canonicalUrlWithoutPagination"
    />

    <a-pagination-links-for-google
      :page="pagination.page"
      :pages-count="pagesCount"
      :non-paginated-url="canonicalUrlWithoutPagination"
    />

    <a-refresh-listener
      :articles.sync="articles"
      :feed-refresh-in-progress.sync="feedRefreshInProgress"
      :slot-refresh-in-progress.sync="slotRefreshInProgress"
      :fetch-data-fn="fetchLoadedArticles"
      :target-tag-slug="$route.params.tag"
      :settings="$options.consts.AUTO_REFRESH_SETTINGS.TAG_PAGE"
      @feed-refreshed="$_disqusCommentCount_updateCommentCounts"
    />
  </div>
</template>

<script>
import { mapActions } from 'vuex'

import mixins from '@/utils/mixins'
import TagInfo from '@/components/_sections/tag/TagInfo'
import AArticleList from 'shared/AArticleList'
import AUrlPagination from 'shared/AUrlPagination'
import { SPINNER_ALIGNMENT } from 'shared/AInProgress'
import ARefreshListener from 'shared/ARefreshListener'
import { throwPageNotFoundError } from '@/plugins/error'
import { getPageNumberFromRoute, createPrefilledArray } from '@/plugins/helper'
import { ROUTE_NAME } from '@/utils/enums/routes'
import {
  generatePaginatedCanonicalUrl,
  checkIfNonCanonicalPath
} from '@/utils/helpers/canonical-links'
import { generateTagUrl } from '@/utils/helpers/url-generators'
import ABanner from 'shared/ABanner'
import {
  BANNER_SETTINGS,
  getInsideListMpuBannerSettingsKey
} from 'enums/banners/banner-settings'
import { PAGE_SCHEME_TYPE } from 'enums/pageSchemes'
import { HTML_TAG } from '@/utils/helpers/processHtml/enums'
import { NEWS_ITEM_DATA_ATTRIBUTE } from 'enums/data-attributes'
import { DISQUS_SOURCE } from 'shared/AActionIcon/_shared/CommentCount/enums'
import APaginationLinksForGoogle from 'shared/APaginationLinksForGoogle'
import { AUTO_REFRESH_SETTINGS } from 'enums/article-auto-refresh'
import { METHOD_NAME_TO_RUN_ON_LOGIN } from '@/utils/mixins/run-method-on-login-logout'
import { METHOD_NAME, PENDING_ACTION_EVENT } from 'enums/pending-actions'
import { redirectHandler } from '@/utils/helpers/redirects/helpers'
import AArticleSlot from 'shared/AArticleSlot/index.vue'
import AViewMore from 'shared/AViewMore/index.vue'
import { VIEW_MORE_STYLE } from 'shared/AViewMore/enums'
import { applyFallback } from '@/utils/helpers'
import { pick } from 'ramda'

const HYDRATED_SECTION_NAME = {
  TAG_INFO: 'TAG_INFO'
}

const TAG_MODEL = {
  Id: null,
  Name: '',
  IsMigrated: false,
  CompanyInfo: {
    Id: null,
    Name: '',
    Description: '',
    Slug: '',
    LogoUrl: ''
  },
  TermInfo: {
    Id: null,
    Description: '',
    Slug: ''
  }
}

const DEFAULT_TAKE = 15
const BANNER_POSITIONS = [6, 9]

const ARTICLE_SLOT_SELECTOR = '.tag-page__article'

function generateDefaultTagPageContentDataModel() {
  return {
    tag: {},
    articles: [],
    pagesCount: 0
  }
}

async function getTagPageData({ store, route }) {
  const { dispatch } = store
  const tagSlug = route.params.tag.toLowerCase()

  const initialPage = getPageNumberFromRoute(route)
  const pagination = {
    take: DEFAULT_TAKE,
    page: initialPage
  }

  const requests = [
    dispatch('tag/requestTagBySlug', { tagSlug }),
    dispatch('tag/requestArticleByTagSlug', { tagSlug, ...pagination })
  ]

  const [Tag, { Articles, PagesCount }] = await Promise.all(requests)

  return {
    tag: applyFallback(Tag, TAG_MODEL),
    tagSlug: Tag.Slug,
    articles: Articles,
    pagesCount: PagesCount,
    initialPage,
    pagination,
    isInitialPagePaginated: !!initialPage
  }
}

export default {
  name: 'TagPage',
  scrollToTop: true,
  validate({ params: { tag } }) {
    return !!tag
  },
  mixins: [
    mixins.runMethodOnLoginLogout,
    mixins.refreshDataHandler,
    mixins.articleDataHandler,
    mixins.disqusCommentCountMixinFactory({ source: DISQUS_SOURCE.IL }),
    mixins.pendingAuthActionsListenersFactory({
      [PENDING_ACTION_EVENT.REFRESH_FOLLOW_TAG]:
        METHOD_NAME.UPDATE_IS_FOLLOWED_TAG_VALUE
    }),
    mixins.delayedContentRefresh,
    mixins.lazyHydratedComponentState
  ],
  components: {
    AViewMore,
    AArticleSlot,
    ABanner,
    TagInfo,
    AArticleList,
    AUrlPagination,
    ARefreshListener,
    APaginationLinksForGoogle
  },
  consts: {
    NEWS_ITEM_DATA_ATTRIBUTE,
    AUTO_REFRESH_SETTINGS,
    VIEW_MORE_STYLE,
    HYDRATED_SECTION_NAME
  },
  head() {
    const { Name, CompanyInfo, TermInfo } = this.tag
    const link = this.$helper.getFullPath(this)
    const description = CompanyInfo.Description || TermInfo.Description || Name

    const webPageSchemeData = {
      relatedLink: link,
      significantLink: link,
      alternativeHeadline: Name,
      headline: Name,
      keywords: Name,
      text: description,
      description,
      mainEntityOfPage: 'Tag page',
      name: Name
    }

    return this.$generateMetaTags({
      titleParam: Name,
      descriptionParam: Name,
      canonicalUrl: this.canonicalUrl,
      schemes: [
        { type: PAGE_SCHEME_TYPE.ORGANIZATION },
        { type: PAGE_SCHEME_TYPE.WEB_PAGE, data: webPageSchemeData }
      ]
    })
  },
  async asyncData(ctx) {
    const { path: currentPath, fullPath } = ctx.route

    try {
      const data = await getTagPageData({ store: ctx.store, route: ctx.route })

      if (data.pagesCount && data.initialPage > data.pagesCount - 1) {
        return throwPageNotFoundError(ctx, 'TagPage')
      }

      const canonicalUrlWithoutPagination = generateTagUrl(
        { slug: data.tag.Slug },
        ctx
      )
      const canonicalUrl = generatePaginatedCanonicalUrl({
        canonicalUrlWithoutPagination,
        path: currentPath
      })

      if (checkIfNonCanonicalPath(ctx, { currentPath, canonicalUrl })) {
        redirectHandler({ ctx, to: canonicalUrl })
        return
      }

      return {
        ...data,
        canonicalUrl,
        canonicalUrlWithoutPagination,
        feedRefreshInProgress: false,
        slotRefreshInProgress: createPrefilledArray(data.articles.length, false)
      }
    } catch (err) {
      ctx.app.$errorHandler(err, ctx, {
        componentName: 'TagPage',
        showErrorPage: true,
        showMessage: false
      })
    } finally {
      await ctx.store.dispatch('router/removeRouteFromQueue', fullPath)
    }
  },
  data() {
    const initialPage = getPageNumberFromRoute(this.$route)

    return {
      ...generateDefaultTagPageContentDataModel(),
      canonicalUrl: this.$route.path,
      canonicalUrlWithoutPagination: this.$route.path,
      ARTICLE_SLOT_SELECTOR,
      SPINNER_ALIGNMENT,
      tagSlug: '',
      pagination: {
        take: DEFAULT_TAKE,
        page: initialPage
      },
      initialPage,
      inProgress: false,
      feedRefreshInProgress: false,
      slotRefreshInProgress: [],
      isInitialPagePaginated: false,
      HTML_TAG
    }
  },
  computed: {
    isRequestInProgress() {
      return this.inProgress || this.isPendingContentRefresh
    },
    breadcrumbsSettings() {
      return {
        [ROUTE_NAME.TAG_TAG]: { nameHandler: () => this.tag.Name }
      }
    },
    isNoNewsMessageVisible() {
      return !this.inProgress && this.articles && this.articles.length === 0
    }
  },
  methods: {
    ...mapActions({
      requestTagBySlug: 'tag/requestTagBySlug',
      requestArticleByTagSlug: 'tag/requestArticleByTagSlug'
    }),
    getArticleSlotProps(article) {
      return {
        ...article,
        [this.$options.consts.NEWS_ITEM_DATA_ATTRIBUTE]: true
      }
    },
    updateIsFollowedTagValues({ value }) {
      this.tag.IsFollowed = value
    },
    isBannerVisible(index) {
      return BANNER_POSITIONS.includes(index + 1)
    },
    getBannerSettings(index) {
      if (!this.isBannerVisible(index)) return null

      const bannerPosition = index + 1
      const bannerNumber = BANNER_POSITIONS.indexOf(bannerPosition) + 1

      return BANNER_SETTINGS.TAG_PAGE[
        getInsideListMpuBannerSettingsKey(bannerNumber)
      ]
    },
    async getTagArticles() {
      try {
        this.inProgress = true
        const tagSlug = this.$route.params.tag.toLowerCase()
        const requests = [
          this.requestArticleByTagSlug({ tagSlug, ...this.pagination }),
          ...(this.tag ? [] : [this.requestTagBySlug({ tagSlug })])
        ]
        const [{ Articles, PagesCount }, Tag] = await Promise.all(requests)
        if (Tag) {
          this.tag = Tag
        }
        this.pagesCount = PagesCount
        this.articles = [...this.articles, ...Articles]
        this.slotRefreshInProgress = createPrefilledArray(
          this.articles.length,
          false
        )
      } catch (err) {
        this.$errorHandler(err, this)
      } finally {
        this.inProgress = false
      }
    },
    getNextArticleChunk() {
      this.pagination.page++
      this.getTagArticles()
    },
    async fetchLoadedArticles() {
      const tagSlug = this.$route.params.tag.toLowerCase()
      const articleChunks = await this.$_articleDataHandler_fetchLoadedArticles(
        {
          action: this.requestArticleByTagSlug,
          initialPage: this.initialPage,
          take: this.pagination.take,
          length: this.articles.length,
          payload: { tagSlug }
        }
      )
      return articleChunks.reduce(
        (acc, { Articles }) => [...acc, ...Articles],
        []
      )
    },
    async [METHOD_NAME_TO_RUN_ON_LOGIN]() {
      const payload = { tagSlug: this.tagSlug }

      try {
        this.tag.IsFollowed = await this.$_refreshDataHandler_getIsFollowedTagValue(
          payload
        )
      } catch (err) {
        this.$errorHandler(err, this)
      }
    },
    [METHOD_NAME.UPDATE_IS_FOLLOWED_TAG_VALUE]({ id, value }) {
      if (this.tag.Id !== id) return

      this.tag.IsFollowed = value
    },
    refreshTagPageContent() {
      const dataKeysForDelayedContentRefresh = Object.keys(
        generateDefaultTagPageContentDataModel()
      )

      this.$_delayedContentRefresh_run({
        getOldDataFn: () => pick(dataKeysForDelayedContentRefresh, this),
        requestNewDataFn: async () => {
          const newData = await getTagPageData({
            route: this.$route,
            store: this.$store
          })

          return pick(dataKeysForDelayedContentRefresh, newData)
        },
        dataComparisonKeysToOmitDeep: ['lastModified', 'ModifiedOn'],
        dataChangedCallbackFn: newData => {
          Object.assign(this, pick(dataKeysForDelayedContentRefresh, newData))

          this.$_lazyHydratedComponentState_rerenderNonHydratedComponents(
            Object.values(HYDRATED_SECTION_NAME)
          )

          const componentIdentifiers = this.articles.map(
            article => article.articleId
          )

          this.$_lazyHydratedComponentState_rerenderNonHydratedComponents(
            componentIdentifiers
          )
        }
      })
    }
  },
  mounted() {
    this.refreshTagPageContent()
  }
}
</script>

<style lang="scss" scoped>
.tag-page__wrapper {
  .tag-page__container {
    margin-top: 20px;
  }

  .tag-page__article {
    margin-bottom: 20px;
  }

  /deep/ .article-list__item-wrapper:last-child .tag-page__article {
    margin-bottom: 0;
  }

  .tag-page__banner {
    margin-top: 24px;
    margin-bottom: 24px;
  }

  .tag-page__view-more {
    $margin: (
      $above-desktop-xl: 20px 0 60px,
      $tablet: 20px 0 40px,
      $mobile: 20px 0 24px
    );

    @include setStyleByBreakpoint('margin', $margin);
  }

  .tag-page__no-news-found {
    text-align: center;
  }
}
</style>
